import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiErrorDetail, CATEGORIES } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Loader2, Send } from "lucide-react";

const EMPTY = { full_name: "", company: "", email: "", phone: "", product_interest: "", torque_requirements: "", message: "" };

export default function Enquiry() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const { user } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (user) {
      setForm((f) => ({ ...f, full_name: f.full_name || user.name || "", email: f.email || user.email || "" }));
    }
  }, [user]);

  useEffect(() => {
    const model = location.state?.partnerModel || location.state?.cubemars;
    if (model) {
      setForm((f) => ({
        ...f,
        message: f.message || `I'm interested in the ${model}. Please share the datasheet, pricing and lead time for India.`,
      }));
    }
  }, [location.state]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and project details.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/enquiries", form);
      toast.success(data.message || "Enquiry received. Our team will contact you shortly.");
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send. Try again.");
    } finally {
      setSending(false);
    }
  };

  const field = "w-full bg-ink border border-white/10 px-4 py-3 text-white placeholder:text-slate-600 font-body text-sm focus:border-signal focus:outline-none transition-colors";
  const label = "font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-2 block";

  return (
    <section id="enquiry" className="relative py-24 lg:py-36 tech-grid" data-testid="enquiry">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
        <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
          transition={{ duration: 0.7 }} className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-5">
            <span className="w-10 h-px bg-signal" />
            <span className="font-mono text-[11px] tracking-[0.35em] uppercase text-signal">RFQ / Enquiry</span>
          </div>
          <h2 className="font-head font-black uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] text-white mb-6">
            Request a<br /><span className="text-signal">datasheet & quote.</span>
          </h2>
          <p className="text-slate-400 leading-relaxed mb-8">
            Connect with <span className="text-white font-semibold">ButinderSingh Matharu</span> and
            the engineering team in Vadodara. Share your torque, ratio and integration requirements — we'll respond with
            matched components and pricing.
          </p>
          <div className="space-y-3 font-mono text-sm">
            <a href="tel:+918866671327" className="block text-slate-300 hover:text-signal transition-colors" data-testid="enquiry-phone">+91-8866671327</a>
            <a href="mailto:info@dashmeshtech.com" className="block text-slate-300 hover:text-signal transition-colors" data-testid="enquiry-email">info@dashmeshtech.com</a>
          </div>
        </motion.div>

        <motion.form onSubmit={submit} initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
          transition={{ duration: 0.7 }} className="lg:col-span-7 bg-card border border-white/10 p-8 lg:p-10" data-testid="enquiry-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className={label}>Full Name *</label>
              <input className={field} value={form.full_name} onChange={set("full_name")} data-testid="input-full-name" placeholder="Your name" />
            </div>
            <div>
              <label className={label}>Company / Lab</label>
              <input className={field} value={form.company} onChange={set("company")} data-testid="input-company" placeholder="Organization" />
            </div>
            <div>
              <label className={label}>Work Email *</label>
              <input type="email" className={field} value={form.email} onChange={set("email")} data-testid="input-email" placeholder="you@company.com" />
            </div>
            <div>
              <label className={label}>Contact Number</label>
              <input className={field} value={form.phone} onChange={set("phone")} data-testid="input-phone" placeholder="+91…" />
            </div>
            <div>
              <label className={label}>Product Category</label>
              <select className={field} value={form.product_interest} onChange={set("product_interest")} data-testid="input-product-interest">
                <option value="">Select…</option>
                {CATEGORIES.filter((c) => c !== "All Products").map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className={label}>Torque / Specs</label>
              <input className={field} value={form.torque_requirements} onChange={set("torque_requirements")} data-testid="input-torque" placeholder="e.g. 45 Nm, 100:1" />
            </div>
          </div>
          <div className="mt-5">
            <label className={label}>Project Details & Quantity *</label>
            <textarea rows={4} className={field} value={form.message} onChange={set("message")} data-testid="input-message" placeholder="Describe your application, quantity and timeline…" />
          </div>
          <button type="submit" disabled={sending} data-testid="enquiry-submit"
            className="group mt-6 w-full font-head font-bold uppercase tracking-wider px-6 py-4 bg-signal text-ink hover:bg-signal-hover transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
            {sending ? <><Loader2 className="w-5 h-5 animate-spin" /> Sending…</> : <>Submit Enquiry <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></>}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
